import {
  ChevronDownIcon,
  ChevronUpIcon,
  GraphiQLProvider,
  PrettifyIcon,
  QueryEditor,
  ResponseEditor,
  Spinner,
  ToolbarButton,
  Tooltip,
  UnStyledButton,
  useGraphiQL,
  useGraphiQLActions,
  VariableEditor
} from '@graphiql/react';
import type { Storage } from '@graphiql/toolkit';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { getGraphQLQueryString, prettifyQuery } from '@takeshape/graphql';
import type { ProjectSchemaJSON } from '@takeshape/schema';
import TakeShapeLogo from 'app/common/components/logo/takeshape-logo';
import { ServiceIcon } from 'app/common/components/service-list';
import { filterOptions } from 'app/common/components/widgets/autocomplete/match-sorter-filter-options';
import type { QueryOption } from 'app/common/components/widgets/select-query/select-query-hook';
import { TAKESHAPE_THEME_NAME } from 'app/common/monaco-theme';
import { branding } from 'app/config';
import { findProvider } from 'app/services/providers';
import { parse, type VariableDefinitionNode } from 'graphql';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ToolQueryEditorProps } from './tool-query-editor-hook';

const nullStorage: Storage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
  get length() {
    return 0;
  },
  clear: () => {}
};

function getRequiredVariableNames(queryText: string): string[] {
  try {
    const doc = parse(queryText);
    const operation = doc.definitions.find((d) => d.kind === 'OperationDefinition');
    if (!operation || operation.kind !== 'OperationDefinition') return [];
    return (operation.variableDefinitions ?? [])
      .filter((v: VariableDefinitionNode) => v.type.kind === 'NonNullType')
      .map((v: VariableDefinitionNode) => v.variable.name.value);
  } catch {
    return [];
  }
}

function buildScaffoldedVariables(requiredNames: string[], currentText: string): string | null {
  let current: Record<string, unknown> = {};
  try {
    current = currentText ? JSON.parse(currentText) : {};
  } catch {
    // Invalid JSON
  }

  const missing = requiredNames.filter((name) => current[name] === undefined || current[name] === null);
  if (missing.length === 0) return null;

  for (const name of missing) {
    if (!(name in current)) {
      current[name] = null;
    }
  }
  return JSON.stringify(current, null, 2);
}

function generateQueryString(schemaJson: ProjectSchemaJSON, option: QueryOption): string {
  try {
    const queryRef = option.ref;
    return prettifyQuery(
      getGraphQLQueryString(schemaJson, queryRef, {
        maxQueryDepth: 2,
        varsForMissing: true
      })
    );
  } catch {
    return '';
  }
}

const queryOptionFilterOptions = filterOptions<QueryOption>(['name', 'namespace']);

function getOptionLabel(option: QueryOption) {
  return option.namespace ? `${option.namespace}_${option.name}` : option.name;
}

function getProviderAvatar(providerName: string) {
  if (providerName === 'takeshape') {
    return (
      <ServiceIcon key={providerName} title={branding.productName}>
        <TakeShapeLogo />
      </ServiceIcon>
    );
  }

  const provider = findProvider(providerName);
  if (provider) {
    return <ServiceIcon key={providerName} src={provider.avatarPath} title={provider.title} />;
  }

  return null;
}

function renderQueryOption(attributes: React.HTMLAttributes<HTMLLIElement>, option: QueryOption) {
  const isMutation = option.ref.includes('Mutation.');
  return (
    <li {...attributes} key={option.ref}>
      <Box display="flex" alignItems="center" sx={{ gap: 1, width: '100%' }}>
        <Typography
          variant="caption"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: '4px !important',
            fontSize: '0.625rem',
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            color: isMutation ? '#66bb6a' : '#42a5f5',
            border: '1px solid',
            borderColor: isMutation ? '#66bb6a' : '#42a5f5'
          }}
        >
          {isMutation ? 'Mutat' : 'Query'}
        </Typography>
        {getProviderAvatar(option.provider)}
        <Typography variant="body2" sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {getOptionLabel(option)}
        </Typography>
      </Box>
    </li>
  );
}

function QueryPicker({
  options,
  schemaJson,
  onSelect
}: {
  options: QueryOption[];
  schemaJson: ProjectSchemaJSON | undefined;
  onSelect: (queryString: string) => void;
}) {
  const handleChange = useCallback(
    (_event: SyntheticEvent, value: QueryOption | null) => {
      if (!value || !schemaJson) return;
      const queryString = generateQueryString(schemaJson, value);
      if (queryString) {
        onSelect(queryString);
      }
    },
    [schemaJson, onSelect]
  );

  // Sort by group so MUI Autocomplete doesn't repeat group headers
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => {
        const aIsMutation = a.ref.includes('Mutation.') ? 1 : 0;
        const bIsMutation = b.ref.includes('Mutation.') ? 1 : 0;
        return aIsMutation - bIsMutation || a.name.localeCompare(b.name);
      }),
    [options]
  );

  return (
    <Autocomplete<QueryOption, false, false, false>
      options={sortedOptions}
      onChange={handleChange}
      value={null}
      blurOnSelect
      filterOptions={queryOptionFilterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderQueryOption}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search queries and mutations..."
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          sx={{ px: 0.5, py: 0.5 }}
          size="small"
        />
      )}
      sx={{
        '& .MuiAutocomplete-endAdornment': { display: 'none' },
        '& .MuiInput-root': { fontSize: '0.8125rem' }
      }}
    />
  );
}

function ToolQueryEditorInner({
  handleQueryEdit,
  queryOptions,
  activeRuntimeSchemaJSON
}: {
  handleQueryEdit: (query?: string) => void;
  queryOptions: QueryOption[];
  activeRuntimeSchemaJSON: ProjectSchemaJSON | undefined;
}) {
  const { run } = useGraphiQLActions();
  const isFetching = useGraphiQL((state) => state.isFetching);
  const queryEditor = useGraphiQL((state) => state.queryEditor);
  const variableEditor = useGraphiQL((state) => state.variableEditor);
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const pendingVariables = useRef<string | null>(null);

  const toggleVariables = useCallback(() => {
    setVariablesOpen((prev) => !prev);
  }, []);

  const handlePrettify = useCallback(() => {
    if (!queryEditor) return;
    try {
      queryEditor.setValue(prettifyQuery(queryEditor.getValue()));
    } catch {
      // Query may not be valid GraphQL yet
    }
  }, [queryEditor]);

  const closeResults = useCallback(() => {
    setResultsOpen(false);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }, []);

  useEffect(() => {
    if (variableEditor && pendingVariables.current !== null) {
      variableEditor.setValue(pendingVariables.current);
      pendingVariables.current = null;
    }
  }, [variableEditor]);

  const handleRun = useCallback(() => {
    const queryText = queryEditor?.getValue() ?? '';
    const requiredNames = getRequiredVariableNames(queryText);

    if (requiredNames.length > 0) {
      const currentVarsText = variableEditor?.getValue() ?? pendingVariables.current ?? '';
      const scaffolded = buildScaffoldedVariables(requiredNames, currentVarsText);

      if (scaffolded !== null) {
        pendingVariables.current = scaffolded;
        if (variableEditor) {
          variableEditor.setValue(scaffolded);
        }
        setVariablesOpen(true);
        return;
      }
    }

    setResultsOpen(true);
    setTimeout(() => {
      run();
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }, [run, queryEditor, variableEditor]);

  const handleQuerySelect = useCallback(
    (queryString: string) => {
      if (queryEditor) {
        queryEditor.setValue(queryString);
      }
      handleQueryEdit(queryString);
    },
    [queryEditor, handleQueryEdit]
  );

  const VariablesIcon = variablesOpen ? ChevronDownIcon : ChevronUpIcon;

  return (
    <Tooltip.Provider>
      <div className="graphiql-container flex-1 min-h-0">
        <div className="graphiql-main flex flex-1 min-h-0">
          <div className="graphiql-sessions flex flex-1 min-w-0 flex-col">
            {/* Query picker */}
            <div className="border-b border-base-300">
              <QueryPicker options={queryOptions} schemaJson={activeRuntimeSchemaJSON} onSelect={handleQuerySelect} />
            </div>

            <div role="tabpanel" id="graphiql-session" className="flex flex-1 min-h-0">
              {/* Query editor + Variables */}
              <div className="graphiql-editors flex flex-col flex-1 min-w-0">
                <section
                  className="graphiql-query-editor relative flex flex-1 min-h-0 overflow-hidden"
                  aria-label="Operation Editor"
                >
                  <QueryEditor onEdit={handleQueryEdit} />
                  <div
                    role="toolbar"
                    aria-label="Editor Commands"
                    className="absolute top-2 right-2 z-10 flex gap-1 bg-white/90 p-1 rounded"
                  >
                    <ToolbarButton onClick={handlePrettify} label="Prettify query">
                      <PrettifyIcon className="graphiql-toolbar-icon" aria-hidden="true" />
                    </ToolbarButton>
                    <IconButton
                      size="small"
                      onClick={handleRun}
                      disabled={isFetching}
                      aria-label="Execute query"
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'common.white',
                        borderRadius: '4px !important',
                        '&:hover': { backgroundColor: 'primary.dark' },
                        '&:disabled': { backgroundColor: 'grey.400', color: 'common.white' },
                        width: 32,
                        height: 32
                      }}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </div>
                </section>

                <div className="graphiql-editor-tools">
                  <UnStyledButton
                    type="button"
                    className={variablesOpen ? 'active' : undefined}
                    onClick={toggleVariables}
                    data-name="variables"
                  >
                    Variables
                  </UnStyledButton>
                  <UnStyledButton
                    type="button"
                    onClick={toggleVariables}
                    aria-label={variablesOpen ? 'Hide variables' : 'Show variables'}
                    className="graphiql-toggle-editor-tools"
                  >
                    <VariablesIcon className="graphiql-chevron-icon" aria-hidden="true" />
                  </UnStyledButton>
                </div>

                <div
                  data-tool-variables
                  style={variablesOpen ? { minHeight: 80, maxHeight: 200, display: 'flex' } : { display: 'none' }}
                >
                  <VariableEditor />
                </div>
              </div>

              {/* Response panel */}
              {resultsOpen && (
                <>
                  <div className="w-px bg-base-300 shrink-0" />
                  <div className="graphiql-response relative flex flex-1 min-w-0 flex-col">
                    <IconButton
                      size="small"
                      onClick={closeResults}
                      aria-label="Close results"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        zIndex: 1,
                        opacity: 0.6,
                        '&:hover': { opacity: 1 }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    {isFetching && <Spinner />}
                    <ResponseEditor />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Tooltip.Provider>
  );
}

const editorSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 300,
  overflow: 'hidden',
  '& *': {
    borderRadius: '0 !important',
    boxShadow: 'none !important'
  },
  '.graphiql-toolbar-button': {
    borderRadius: '4px !important'
  },
  '.graphiql-container': {
    flex: '1 1 0%',
    minHeight: 0,
    backgroundColor: 'transparent !important'
  },
  '.graphiql-main, .graphiql-sessions, .graphiql-editors, .graphiql-response, .graphiql-query-editor, .graphiql-editor-tool':
    {
      backgroundColor: 'transparent !important',
      background: 'transparent !important',
      margin: '0 !important',
      border: 'none !important'
    },
  '.graphiql-session-header': {
    display: 'none'
  },
  '.graphiql-editor-tools': {
    borderTop: '1px solid #e0e0e0 !important',
    cursor: 'default !important'
  }
} as const;

export const ToolQueryEditor = (props: ToolQueryEditorProps) => {
  const { path, query, schema, fetcher, handleQueryEdit, queryOptions, activeRuntimeSchemaJSON } = props;

  const storage = useMemo(() => nullStorage, []);

  return (
    <Box data-testid={`tool-query-editor-${path}`} className="border border-base-300" sx={editorSx}>
      <GraphiQLProvider
        fetcher={fetcher}
        schema={schema}
        defaultQuery={query}
        storage={storage}
        editorTheme={{ light: TAKESHAPE_THEME_NAME, dark: TAKESHAPE_THEME_NAME }}
      >
        <ToolQueryEditorInner
          handleQueryEdit={handleQueryEdit}
          queryOptions={queryOptions}
          activeRuntimeSchemaJSON={activeRuntimeSchemaJSON}
        />
      </GraphiQLProvider>
    </Box>
  );
};
